import { SelectionModel } from '@angular/cdk/collections';
import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { debounceTime, filter, finalize, take } from 'rxjs/operators';

import { UserService } from 'src/app/core/api/user.service';

import { CohortService } from '../../../core/api/cohort.service';
import { SiteService } from '../../../core/api/site.service';
import { UtilitiesService } from '../../../core/api/utlities.service';
import { SiteHelperService } from '../../../core/helper/site.service';
import { DialogChangeSiteStateComponent } from '../../../shared/components/dialog-change-site-state/dialog-change-site-state.component';
import { SiteReviewCheckEnums } from '../../../shared/constants/site_review_check.enums';
import { Site, User } from '../../../shared/models';
import { SpinnerService } from '../../../shared/spinner/spinner.service';
import * as fromApp from '../../../store';
import { setDashboardFilter } from '../../../store/admin/admin.actions';
import { selectAdminFiltersAndPage } from '../../../store/admin/admin.selectors';
import { selectCurrentUser } from '../../../store/auth/auth.selectors';
import { AdminTableBase } from '../admin-table-base/admin-table-base';
import { StatusCategoryEnum } from "../../../shared/constants/site_status_category.enum";

@Component({
  selector: 'fc-checklist-dashboard',
  templateUrl: './checklist-dashboard.component.html',
  styleUrls: ['./checklist-dashboard.component.scss'],
})
export class ChecklistDashboardComponent extends AdminTableBase implements OnInit {
  destroyRef = inject(DestroyRef);
  math = Math;
  readonly PAGE_NAME: string = 'checklist';
  @ViewChild('filterName', { static: false }) filterName: any;
  @ViewChild('filterId', { static: false }) filterId: any;
  @ViewChild('filterOwner', { static: false }) filterOwner: any;
  @ViewChild('filterDelegate', { static: false }) filterDelegate: any;
  @ViewChild('showTerminated', { static: false }) showTerminated: any;
  @ViewChild('assignedToMe', { static: false }) assignedToMe: any;

  SiteReviewCheckEnums = SiteReviewCheckEnums;
  possibleValues = ['Yes', 'No', 'Deferred'];
  reviewChecks = [
    {
      name: 'ownership',
      display: 'Ownership',
      check: SiteReviewCheckEnums.OwnerNamesAndParcelIDsVerified,
    },
    {
      name: 'covenants',
      display: 'Restricted',
      check: SiteReviewCheckEnums.RestrictedConvenants,
    },
    {
      name: 'change',
      display: 'RSA',
      check: SiteReviewCheckEnums.GoogleEarthEngineChangeDetectionVerified,
    },
    {
      name: 'map',
      display: 'Map',
      check: SiteReviewCheckEnums.MapApproved,
    },
    {
      name: 'program',
      display: 'Programs',
      check: SiteReviewCheckEnums.AllSupportingDocumentsProvided,
    },
    {
      name: 'docs',
      display: 'Buyer Docs',
      check: SiteReviewCheckEnums.BuyerSpecificRequirementDocument,
    },
  ];

  displayedColumns: string[] = [
    'select',
    'id',
    'status',
    'submittedDate',
    'name',
    'needsReview',
    'ownership',
    'covenants',
    'change',
    'map',
    'program',
    'docs',
  ];

  statuses = [];
  sortStatusArray = [];

  // names of columns must match backend column names
  sortBy = {
    submitted_date: this.sortStates.untouched,
    status: this.sortStates.untouched,
    id: this.sortStates.untouched,
    name: this.sortStates.untouched,
  };

  dataSource: MatTableDataSource<Site>;
  selection = new SelectionModel<Site>(true, []);

  enableBulkSiteStateChangeButton = false;

  sites: Array<Site>;
  admins: User[];

  reviewCheckQuery = '';
  needsReviewQuery = '';

  filterValues = {
    name: '',
    id: '',
    status: [],
    assigned_to_me: false,
    needs_review: null,
    assigned_admin_id: null,
    reviewCheckFilter: null,
  };
  defaultStatusQuery = '';
  reviewCheckFilterValue = {};
  reviewCheckFilterName: string = null;

  isLimitedAdmin = false;

  applyFilterStatusSubject$: Subject<{ value: any }> = new Subject();
  applyFilterAssignedToMeSubject$: Subject<{ event: Event; column: string }> = new Subject();
  applyFilterNeedsReviewSubject$: Subject<{ event: Event; column: string }> = new Subject();
  applyReviewCheckFilterSubject$: Subject<{ event: Event; column: string }> = new Subject();

  selectAdminFiltersAndPage$ = this.store
    .select(selectAdminFiltersAndPage)
    .pipe(take(1), takeUntilDestroyed(this.destroyRef));

  constructor(
    public dialog: MatDialog,
    readonly siteService: SiteService,
    readonly siteHelper: SiteHelperService,
    readonly store: Store<fromApp.AppState>,
    readonly utilitiesService: UtilitiesService,
    readonly router: Router,
    readonly userService: UserService,
    readonly cohortService: CohortService,
    readonly spinnerService: SpinnerService
  ) {
    super();
  }

  ngOnInit() {
    this.sortStatusArray = this.siteHelper.siteUnderReviewStatusList;
    this.defaultStatusQuery = '&status=' + this.utilitiesService.preprocessFilterStrings(this.sortStatusArray);
    this.applyReviewCheckFilterSubject$
      .pipe(debounceTime(this.FILTER_DELAY), takeUntilDestroyed(this.destroyRef))
      .subscribe((eventObj: { event: any; column: string }) => {
        this.reviewCheckFilterName = eventObj.column;
        this.filterValues.reviewCheckFilter = eventObj.event.value;
        for (let key in this.reviewCheckFilterValue) {
          if (key !== eventObj.column) {
            this.reviewCheckFilterValue[key] = null;
          }
        }
        this.doGetSiteReviewCheckField(eventObj.event.value, eventObj.column);
      });

    this.applyFilterNeedsReviewSubject$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((eventObj: { event: any; column: string }) => {
        this.doGetSiteByNeedsReviewField(eventObj.event.value, eventObj.column);
      });

    this.applyFilterStatusSubject$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((eventObj: { value: any }) => {
      this.doGetSiteByStatusField(eventObj.value);
    });

    this.applyFilterAssignedToMeSubject$
      .pipe(debounceTime(this.FILTER_DELAY), takeUntilDestroyed(this.destroyRef))
      .subscribe((eventObj: { event: any; column: string }) => {
        const value = eventObj.event.checked.toString();
        this.doGetMySites(value, eventObj.column);
      });

    this.selection.changed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.selection.isEmpty()) {
        this.enableBulkSiteStateChangeButton = false;
      } else {
        this.enableBulkSiteStateChangeButton = this.selection.selected.every(
          (val, i, arr) => val.status === arr[0].status
        );
      }
    });

    this.store
      .select(selectCurrentUser)
      .pipe(
        filter(currentUser => currentUser !== null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(currentUser => {
        this.isLimitedAdmin = currentUser.isLimitedAdmin;
        if (!this.isLimitedAdmin) {
          this.displayedColumns.push('assignedAdmin');
          this.statuses = this.siteHelper.siteStatusList;
        } else {
          this.statuses = this.siteHelper.siteApprovedStatusList;
        }
      });

    this.selectAdminFiltersAndPage$.subscribe(state => {
      let pageFilter = super.setUpAdminFilter(state.filters);
      this.reviewCheckQuery = pageFilter?.reviewCheckQuery || '';
      if (this.reviewCheckQuery != '') {
        this.reviewCheckFilterName = this.reviewCheckQuery.split('=')[1].split(';')[0];
        this.reviewCheckFilterValue[this.reviewCheckFilterName] =
          this.reviewCheckQuery.split('=')[1].split(';')[1] === 'true';
      }
      this.needsReviewQuery = pageFilter?.needsReviewQuery || '';
      this.initializeFilters();
      this.queryItems();
    });

    this.userService.getAdmins().subscribe((admins: User[]) => (this.admins = admins));
  }

  getFullQueryString(): string {
    return (
      this.getBaseQueryString() +
      this.filterQuery +
      (this.statusQuery != '' ? this.statusQuery : this.defaultStatusQuery) +
      this.sortQuery +
      this.showAssignedToMeQuery +
      this.assignedAdminQuery +
      this.reviewCheckQuery +
      this.needsReviewQuery
    );
  }

  applyStatusFilter(): void {
    this.applyFilterStatusSubject$.next({ value: this.filterValues.status });
  }

  applyAssignedToMeFilter(event: any, column: string): void {
    this.applyFilterAssignedToMeSubject$.next({ event, column });
  }

  queryItems(): void {
    // always include pagination
    this.spinnerService.show('app-spinner');
    this.siteService
      .getFilteredSites(this.getFullQueryString())
      .pipe(finalize(() => this.spinnerService.hide('app-spinner')))
      .subscribe(siteResults => {
        this.clearSelection();
        this.sites = siteResults.results;
        this.totalItems = siteResults.total;
        this.setAllPages();
        this.setStartEndIndexes();
        this.dataSource = new MatTableDataSource(this.sites);
      });
  }

  onBulkChangeSiteState(): void {
    const dialogRef = this.dialog.open(DialogChangeSiteStateComponent, {
      panelClass: 'fc-yes-no-dialog',
      data: {
        categories: this.isLimitedAdmin ? [StatusCategoryEnum.APPROVED] : this.siteHelper.allCategoryValues,
        sites: this.selection.selected,
        buttonTextYes: 'Confirm',
        buttonTextNo: 'Cancel',
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.queryItems();
      }
    });
  }

  clickRow(event: MouseEvent, row: Site): void {
    this.router.navigate(['/admin/site', row.id]);
  }

  clearFilters(): void {
    if (this.getBaseQueryString() !== this.getFullQueryString()) {
      super.clearFilters();
      this.statusQuery = '';
      this.showAssignedToMeQuery = '';
      this.reviewCheckQuery = '';
      this.needsReviewQuery = '';
      this.reviewCheckFilterValue = {};
      this.filterValues = {
        name: '',
        id: '',
        status: [],
        assigned_to_me: false,
        needs_review: null,
        assigned_admin_id: null,
        reviewCheckFilter: null,
      };
      this.filterName.nativeElement.value = '';
      this.filterId.nativeElement.value = '';
      this.assignedToMe.checked = false;
      this.queryItems();
    }
  }

  clearSelection(): void {
    super.clearSelection();
    this.enableBulkSiteStateChangeButton = false;
  }

  bulkAction($event: MatSelectChange) {
    if ($event.value === 'status') {
      this.onBulkChangeSiteState();
    } else {
      console.log('No Action found');
    }
    $event.source.value = '';
  }

  getReviewCheck(site: Site, type: SiteReviewCheckEnums) {
    let check = site.siteReviewChecks?.find(check => check.type === type);
    if (check) {
      return check.status;
    } else {
      return null;
    }
  }

  applyNeedsReviewFilter(event: any, column: string): void {
    this.applyFilterNeedsReviewSubject$.next({ event, column });
  }

  applyReviewCheckFilter(event: any, column: string): void {
    this.applyReviewCheckFilterSubject$.next({ event, column });
  }

  doGetSiteReviewCheckField(value: string, field: string): void {
    this.reviewCheckQuery = '';
    if (typeof value !== 'undefined') {
      this.reviewCheckQuery = value.length !== 0 ? `&review_check=${field};${value}` : '';
    }

    this.store.dispatch(
      setDashboardFilter({
        filter: 'reviewCheckQuery',
        value: this.reviewCheckQuery,
        page: this.PAGE_NAME,
      })
    );

    this.resetOffsetAndCurrentPage();
    this.queryItems();
  }

  doGetSiteByNeedsReviewField(value: string, field: string): void {
    this.needsReviewQuery = '';
    if (typeof value !== 'undefined') {
      this.needsReviewQuery = value.length !== 0 ? `&${field}=${value}` : '';
    }

    this.store.dispatch(
      setDashboardFilter({
        filter: 'needsReviewQuery',
        value: this.needsReviewQuery,
        page: this.PAGE_NAME,
      })
    );

    this.resetOffsetAndCurrentPage();
    this.queryItems();
  }
}
